import React from "react"
import styled from "styled-components"
import {sizes} from "../../shared/utils/media";
import useTranslation from "../../shared/utils/translation";
import Logo from "../../shared/components/Logo";
import {
  AiOutlineMail,
  AiOutlinePhone,
  AiOutlineEnvironment,
  AiOutlineLinkedin,
  AiOutlineArrowRight,
} from "react-icons/ai";

const ADDRESS_LINE_1 = "Danavägen 2"
const ADDRESS_LINE_2 = "74652 Bålsta"
const ADDRESS_COUNTRY = "Sweden"
const EMAIL = "info@toppowernordic.se"
const PHONE_DISPLAY = "+46-171 442 300"
const PHONE_TEL = "+4617144300"
const LINKEDIN_URL = "https://www.linkedin.com/company/toppower-nordic/"
const MAP_QUERY = encodeURIComponent(`${ADDRESS_LINE_1}, ${ADDRESS_LINE_2}, ${ADDRESS_COUNTRY}`)
const MAP_EMBED_URL = `https://www.google.com/maps?q=${MAP_QUERY}&output=embed`
const MAP_LINK_URL = `https://www.google.com/maps/dir/?api=1&destination=${MAP_QUERY}`

const Section = styled.section`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
  background: #ffffff;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);

  @media only screen and (max-width: ${sizes.desktop}px) {
    grid-template-columns: 1fr;
    padding: 24px;
    gap: 24px;
  }
`

const Left = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`

const LogoRow = styled.div`
  margin-bottom: 24px;
`

const Heading = styled.h2`
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 20px;
  color: #111;
  letter-spacing: 0.02em;
`

const ContactList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 24px 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
`

const ContactItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-size: 15px;
  line-height: 1.4;
  color: #333;

  svg {
    flex-shrink: 0;
    font-size: 20px;
    margin-top: 2px;
    color: #111;
  }

  a {
    color: inherit;
    text-decoration: none;
    transition: color 150ms ease;

    &:hover {
      color: #555;
      text-decoration: underline;
    }
  }
`

const ContactLabel = styled.span`
  display: block;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #888;
  margin-bottom: 2px;
`

const ContactValue = styled.div`
  font-weight: 600;
`

const Social = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 8px;

  a {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border-radius: 50%;
    background: #f4f4f4;
    color: #111;
    transition: background 150ms ease, transform 150ms ease;

    &:hover {
      background: #111;
      color: #fff;
      transform: translateY(-2px);
    }

    svg {
      font-size: 20px;
    }
  }
`

const MapWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const MapFrame = styled.div`
  position: relative;
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  background: #f4f4f4;
  flex: 1;
  min-height: 280px;

  iframe {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: 0;
    display: block;
  }
`

const DirectionsButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 14px;
  padding: 12px 18px;
  background: #111;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  border-radius: 999px;
  text-decoration: none;
  transition: background 150ms ease, transform 150ms ease;
  align-self: flex-start;

  &:hover {
    background: #333;
    color: #fff;
    transform: translateY(-1px);
  }

  svg {
    font-size: 16px;
  }
`

function HelpYouSection() {
  const t = useTranslation()

  return (
    <Section>
      <Left>
        <div>
          <LogoRow>
            <Logo position={"body"} color={"black"}/>
          </LogoRow>
          <Heading>{t("contact.title")}</Heading>
          <ContactList>
            <ContactItem>
              <AiOutlineEnvironment/>
              <div>
                <ContactLabel>{t("contact.address")}</ContactLabel>
                <ContactValue>
                  {ADDRESS_LINE_1}<br/>{ADDRESS_LINE_2}
                </ContactValue>
              </div>
            </ContactItem>
            <ContactItem>
              <AiOutlineMail/>
              <div>
                <ContactLabel>{t("contact.email")}</ContactLabel>
                <ContactValue>
                  <a href={`mailto:${EMAIL}`}>{EMAIL}</a>
                </ContactValue>
              </div>
            </ContactItem>
            <ContactItem>
              <AiOutlinePhone/>
              <div>
                <ContactLabel>{t("contact.phone")}</ContactLabel>
                <ContactValue>
                  <a href={`tel:${PHONE_TEL}`}>{PHONE_DISPLAY}</a>
                </ContactValue>
              </div>
            </ContactItem>
          </ContactList>
        </div>
        <Social>
          <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
            <AiOutlineLinkedin/>
          </a>
        </Social>
      </Left>

      <MapWrapper>
        <MapFrame>
          <iframe
            title="Map of TopPower Nordic"
            src={MAP_EMBED_URL}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
        </MapFrame>
        <DirectionsButton href={MAP_LINK_URL} target="_blank" rel="noopener noreferrer">
          {t("contact.getDirections")}
          <AiOutlineArrowRight/>
        </DirectionsButton>
      </MapWrapper>
    </Section>
  )
}

export default HelpYouSection
